import { useCallback, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useOrgMembers,
  useOrgRoles,
  useAssignMemberRole,
  useDeactivateOrgMember,
  useReactivateOrgMember,
} from '@/hooks/org-admin';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import { MemberCard, MemberRow } from '@/components/molecules/org-admin/MemberRow';
import { grantableRolesFor } from '@/components/molecules/org-admin/roleOptions';
import { roleChangeBlockedReason } from '@/components/molecules/org-admin/assignableMembers';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
  OrgAdminNoResults,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import {
  OA_NARROW_BREAKPOINT,
  PlainHeader,
  SortableHeader,
  sortRows,
  useContainerWidth,
  useSort,
  useTextFilter,
} from '@/components/molecules/org-admin/OrgAdminTable';
import { OrgAdminResultCount, OrgAdminToolbar } from '@/components/molecules/org-admin/OrgAdminToolbar';
import type { OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import type { OrgMember } from '@/services/org-admin';

export interface MembersPanelProps {
  self: OrgAdminSelfContext;
}

type MemberSortKey = 'email' | 'status' | 'authority' | 'role';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

// Sorting by management level should follow authority, not the alphabet — "admin" before "member"
// before "owner" is a meaningless order for the column an admin is scanning for escalation.
const AUTHORITY_RANK: Record<string, number> = { owner: 0, admin: 1, member: 2 };

function authorityRank(member: OrgMember): number {
  return AUTHORITY_RANK[member.management_level ?? ''] ?? 3;
}

export function MembersPanel({ self }: MembersPanelProps) {
  const { data: members = [], isLoading, isError, error, refetch } = useOrgMembers();
  const { data: roles = [] } = useOrgRoles();
  const assignableRoles = grantableRolesFor(self.currentUserRole, roles);
  const assignRole = useAssignMemberRole();
  const deactivate = useDeactivateOrgMember();
  const reactivate = useReactivateOrgMember();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const { sort, toggle } = useSort<MemberSortKey>({ key: 'email', direction: 'asc' });

  // Layout is chosen from the width of the panel, not of the window — this component renders beside
  // an expandable sidebar, so the viewport tells it nothing useful.
  const { ref: bodyRef, width } = useContainerWidth<HTMLDivElement>();
  const isNarrow = width !== null && width < OA_NARROW_BREAKPOINT;

  const statusFiltered = useMemo(
    () =>
      statusFilter === 'all'
        ? members
        : members.filter((member) =>
            statusFilter === 'active'
              ? member.status.toLowerCase() === 'active'
              : member.status.toLowerCase() !== 'active',
          ),
    [members, statusFilter],
  );

  // Searchable by the two things an admin actually knows about a person: their email and the role
  // they hold ("who are all the Analysts?").
  const searched = useTextFilter(statusFiltered, query, (member) => [member.email, member.role_name]);

  const visible = useMemo(
    () =>
      sortRows(searched, sort, (member, key) => {
        switch (key) {
          case 'email':
            return member.email;
          case 'status':
            return member.status;
          case 'authority':
            return authorityRank(member);
          case 'role':
            return member.role_name;
        }
      }),
    [searched, sort],
  );

  /*
   * Keyed on `userId`, not `memberId`.
   *
   * The retired REST route took the membership row id; `updateOrgUserRole` takes the user id, the
   * same key deactivate/reactivate already use. Passing the wrong one fails server-side with "not a
   * member of this organization", which reads like a permissions problem — so the whole path uses
   * one identifier and the type refuses the other.
   */
  const handleAssignRole = useCallback(
    (userId: string, roleId: string) => {
      assignRole.mutate(
        { userId, roleId },
        {
          onSuccess: () => toast.success('Role updated'),
          onError: (err) => toast.error((err as Error).message || 'Failed to update role'),
        },
      );
    },
    [assignRole],
  );

  /**
   * Deactivation now confirms first.
   *
   * It was a single click on an unlabelled power icon that immediately revoked someone's access to
   * the org — while cancelling an *invitation*, a far less consequential act, did ask. The
   * asymmetry was backwards. Reactivation stays a single click: it grants access back, and is
   * trivially undone by the button that is still there.
   */
  const handleToggleActive = useCallback(
    async (member: OrgMember) => {
      const isActive = member.status.toLowerCase() === 'active';
      if (isActive) {
        const confirmed = await confirmModal.error({
          title: 'Deactivate member?',
          content: `${member.email} will immediately lose access to this organization. Their history and spend are kept, and you can reactivate them at any time.`,
          okText: 'Deactivate',
          okButtonProps: { variant: 'destructive' },
        });
        if (!confirmed) return;
      }
      const mutation = isActive ? deactivate : reactivate;
      mutation.mutate(member.user_id, {
        onSuccess: () => toast.success(isActive ? 'Member deactivated' : 'Member reactivated'),
        onError: (err) => toast.error((err as Error).message || 'Failed to update member'),
      });
    },
    [confirmModal, deactivate, reactivate],
  );

  const rowProps = (member: OrgMember) => ({
    memberId: member.id,
    userId: member.user_id,
    email: member.email,
    managementLevel: member.management_level,
    status: member.status,
    roleId: member.role_id,
    roleName: member.role_name,
    isSelf: member.user_id === self.currentUserId,
    // Depends on the whole roster (is this the last owner?), so it is computed here, not in the row.
    roleChangeBlockedReason: roleChangeBlockedReason(member, members),
    roles: assignableRoles,
    canManage: self.canManage,
    isAssigningRole: assignRole.isPending && assignRole.variables?.userId === member.user_id,
    isTogglingActive:
      (deactivate.isPending && deactivate.variables === member.user_id) ||
      (reactivate.isPending && reactivate.variables === member.user_id),
    onAssignRole: (roleId: string) => handleAssignRole(member.user_id, roleId),
    onToggleActive: () => handleToggleActive(member),
  });

  const hasFilters = query.trim() !== '' || statusFilter !== 'all';
  const canFilter = !isLoading && !isError && members.length > 0;

  return (
    <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
      {/* Search and filters are only offered when there is something to search. A filter bar above
          an error state or an empty roster is dead UI that invites a pointless click. */}
      <OrgAdminToolbar
        searchPlaceholder={canFilter ? 'Search members by email or role' : undefined}
        searchValue={query}
        onSearchChange={canFilter ? setQuery : undefined}
        filters={
          canFilter && (
            <SegmentedControl
              ariaLabel="Filter members by status"
              value={statusFilter}
              options={STATUS_FILTERS}
              onChange={setStatusFilter}
              size="sm"
            />
          )
        }
      />

      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <OrgAdminTableSkeleton
            rows={7}
            columns={['38%', '84px', '72px', '120px']}
            label="Loading members…"
          />
        ) : isError ? (
          <OrgAdminErrorState
            title="Couldn't load members"
            error={error}
            onRetry={() => void refetch()}
          />
        ) : members.length === 0 ? (
          <OrgAdminEmptyState
            icon={Users}
            title="No members yet"
            description="Everyone who accepts an invitation appears here. Invite your first teammate from the Invitations tab."
          />
        ) : visible.length === 0 ? (
          <OrgAdminNoResults
            query={query}
            onClear={() => {
              setQuery('');
              setStatusFilter('all');
            }}
          />
        ) : isNarrow ? (
          <div>
            {visible.map((member) => (
              <MemberCard key={member.id} {...rowProps(member)} />
            ))}
          </div>
        ) : (
          <table className="oa-table">
            <caption className="sr-only">
              Organization members, their status, authority level and assigned role
            </caption>
            <thead>
              <tr>
                <SortableHeader columnKey="email" label="Member" sort={sort} onSort={toggle} />
                <SortableHeader columnKey="status" label="Status" sort={sort} onSort={toggle} width="112px" />
                <SortableHeader columnKey="authority" label="Authority" sort={sort} onSort={toggle} width="122px" />
                <SortableHeader columnKey="role" label="Role" sort={sort} onSort={toggle} width="184px" />
                <PlainHeader label="Actions" align="right" width="52px" srOnly />
              </tr>
            </thead>
            <tbody>
              {visible.map((member) => (
                <MemberRow key={member.id} {...rowProps(member)} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && !isError && members.length > 0 && (
        <OrgAdminResultCount shown={visible.length} total={members.length} noun="member">
          {hasFilters && visible.length !== members.length && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setStatusFilter('all');
              }}
              className="oa-meta inline-flex min-h-6 items-center rounded px-1 transition-colors hover:text-foreground"
            >
              Clear filters
            </button>
          )}
        </OrgAdminResultCount>
      )}

      {confirmModalContext}
    </div>
  );
}
